/**
 * NativeSelect — the native `<select>` control (formerly exported as `Select`;
 * renamed in DS-merge Phase C per merge plan §7.6 coexistence).
 *
 * Use for dense data-entry, canvas widget-editor cells, long option lists, and
 * touch-heavy forms where the OS picker and native form submission win. For
 * primary product surfaces (forms, filters, settings, options with icons or
 * descriptions) use the default system `Select` (headless, from molecules).
 */
import {
  forwardRef,
  useId,
  type SelectHTMLAttributes,
  type ReactNode,
} from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "../lib/utils.js";

export interface NativeSelectOption<TValue extends string = string> {
  readonly value: TValue;
  readonly label: string;
}

/** One `<optgroup>`: a label and its options (PB-5134, the grouped time zones). */
export interface NativeSelectGroup<TValue extends string = string> {
  readonly label: string;
  readonly options: ReadonlyArray<NativeSelectOption<TValue>>;
}

export interface NativeSelectProps<TValue extends string = string> extends Omit<
  SelectHTMLAttributes<HTMLSelectElement>,
  "children" | "value" | "onChange"
> {
  readonly label?: string;
  readonly hint?: string;
  readonly error?: string;
  /** Flat options; rendered before any `groups`. */
  readonly options?: ReadonlyArray<NativeSelectOption<TValue>>;
  /** Grouped options, one `<optgroup>` each. */
  readonly groups?: ReadonlyArray<NativeSelectGroup<TValue>>;
  readonly value: TValue;
  readonly onChange: (value: TValue) => void;
  readonly placeholder?: string;
  readonly leadingIcon?: ReactNode;
}

/**
 * Native `<select>` wrapped to match Input.tsx's surface (label, hint, error,
 * Tailwind tokens). Headless variant is intentionally deferred — when a
 * widget needs richer interactions (search, multi-select) we'll add a
 * Combobox primitive.
 */
export const NativeSelect = forwardRef<HTMLSelectElement, NativeSelectProps>(
  function NativeSelect(
    {
      label,
      hint,
      error,
      options = [],
      groups = [],
      value,
      onChange,
      placeholder,
      leadingIcon,
      className,
      id,
      disabled,
      ...rest
    },
    ref,
  ) {
    const autoId = useId();
    const selectId = id ?? autoId;
    // Mirror Input.tsx: tie hint/error to the control for assistive tech.
    const hasError = !!error;
    const description =
      hasError || hint ? `${selectId}-description` : undefined;
    return (
      <div className="w-full">
        {label ? (
          <label
            htmlFor={selectId}
            className="block text-xs font-medium text-pb-text-secondary mb-1.5"
          >
            {label}
          </label>
        ) : null}
        <div
          className={cn(
            "relative flex items-center gap-2 rounded-md border bg-pb-card transition-colors",
            "px-3 h-9",
            // The text-field focus look (ADR-0092 d8), on the frame while the
            // select inside it is focus-visible.
            error
              ? "border-pb-red/60 focus-frame-invalid"
              : "border-pb-control-border focus-frame",
            disabled ? "opacity-60" : "",
          )}
        >
          {leadingIcon ? (
            <span className="text-pb-text-muted shrink-0">{leadingIcon}</span>
          ) : null}
          <select
            id={selectId}
            ref={ref}
            disabled={disabled}
            value={value}
            aria-invalid={hasError ? true : undefined}
            aria-describedby={description}
            onChange={(e) => onChange(e.target.value)}
            className={cn(
              "flex-1 min-w-0 pe-6 bg-pb-card text-sm text-pb-text focus:outline-none disabled:cursor-not-allowed",
              "appearance-none cursor-pointer",
              // The popup is browser-drawn: Chrome derives its background from
              // the select's own computed `background-color` (a transparent one
              // resolves to white), and the rows inherit the select's colour.
              // Both are painted from tokens so the list stays readable in
              // every theme, and so a consumer `className` background override
              // cannot wash the rows out (PB-4901).
              "[&_option]:bg-pb-card [&_option]:text-pb-text",
              "[&_optgroup]:bg-pb-card [&_optgroup]:text-pb-text-secondary",
              className,
            )}
            {...rest}
          >
            {placeholder ? (
              <option value="" disabled>
                {placeholder}
              </option>
            ) : null}
            {options.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
            {groups.map((group) => (
              <optgroup key={group.label} label={group.label}>
                {group.options.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
          {/* `appearance-none` removes the native arrow, so the affordance is
              drawn here, as the design canvas draws it. */}
          <ChevronDown
            className="pointer-events-none absolute end-3 h-4 w-4 text-pb-text-muted"
            aria-hidden
          />
        </div>
        {hint || hasError ? (
          <p
            id={description}
            className={cn(
              "text-dense-1 mt-1",
              hasError ? "text-pb-red-text" : "text-pb-text-muted",
            )}
          >
            {hasError ? error : hint}
          </p>
        ) : null}
      </div>
    );
  },
) as <TValue extends string = string>(
  props: NativeSelectProps<TValue> & { ref?: React.Ref<HTMLSelectElement> },
) => React.ReactElement;
